import { useEffect } from "react";
import { pollDelay } from "./connectors";

/** Load now, then again on pollDelay's schedule while the tab is visible (OD-15, OD-34): nothing
 *  while hidden, at once on coming back, fast until `fastUntil`. `load` must catch its own errors
 *  (every caller's does), so no poll ever leaves an unhandled rejection. Re-runs (and so loads at
 *  once) when `load` or `fastUntil` changes. */
export function useVisiblePoll(load: () => Promise<void>, fastUntil = 0): void {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let alive = true;
    const schedule = () => {
      clearTimeout(timer);
      const delay = pollDelay(document.visibilityState === "visible", Date.now(), fastUntil);
      if (delay !== null) timer = setTimeout(() => { void load().then(() => { if (alive) schedule(); }); }, delay);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") void load().then(() => { if (alive) schedule(); });
      else clearTimeout(timer);
    };
    void load().then(() => { if (alive) schedule(); });
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load, fastUntil]);
}
